import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getUsers, getUser, updateUserFee, suspendUser } from '../api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function UsersPage() {
  const [users, setUsers]           = useState([]);
  const location = useLocation();
  const urlSearch = new URLSearchParams(location.search).get('q') || '';
  const [search, setSearch] = useState(urlSearch);
  const [selected, setSelected]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [editFee, setEditFee]       = useState('');
  const [editBoost, setEditBoost]   = useState(0);
  const [boostSaving, setBoostSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [boostMsg, setBoostMsg]     = useState('');
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState('');
  const [platformFeeBps, setPlatformFeeBps] = useState(50);
  const [waitlist, setWaitlist]       = useState([]);
  const [selectedWaitlist, setSelectedWaitlist] = useState(null);
  const [spamReports, setSpamReports]   = useState([]);
  const [clearingStrikes, setClearingStrikes] = useState(false);

  useEffect(() => {
    loadUsers();
    fetch(`${API}/platform-settings`)
      .then(r => r.json())
      .then(d => setPlatformFeeBps(d.feeBps || 50))
      .catch(console.error);
  }, [search]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    fetch(`${API}/admin/waitlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(setWaitlist)
      .catch(e => console.error('Waitlist fetch error:', e));
  }, []);

  async function loadUsers() {
    setLoading(true);
    try { const res = await getUsers(search); setUsers(res); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function loadUser(id) {
    setConfirmDelete(false);
    setSelectedWaitlist(null);
    try {
      const res = await getUser(id);
      setSelected(res);
      setEditFee(parseFloat(res.feePct !== null && res.feePct !== undefined ? res.feePct : 0).toString());
      setEditBoost((res.effectiveBoostBps !== undefined ? res.effectiveBoostBps : platformFeeBps) / 100);
      setMsg('');
      // Load spam reports for this user
      const token = localStorage.getItem('admin_token');
      try {
        const r = await fetch(`${API}/admin/users/${id}/reports`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (r.ok) setSpamReports(await r.json());
        else setSpamReports([]);
      } catch (e) { setSpamReports([]); }
    } catch (err) { console.error(err); }
  }

  async function handleFeeUpdate() {
    if (!selected) return;
    setSaving(true);
    try {
      const feeBps = Math.round(parseFloat(editFee) * 100);
      await updateUserFee(selected.id, feeBps);
      setMsg(`Fee updated to ${editFee}%`);
      loadUser(selected.id); loadUsers();
    } catch (err) { setMsg('Failed: ' + err.message); }
    finally { setSaving(false); }
  }

  async function handleResetFee() {
    if (!selected) return;
    setSaving(true);
    try {
      await updateUserFee(selected.id, -1);
      setMsg('Reset to platform fee');
      loadUser(selected.id); loadUsers();
    } catch (err) { setMsg('Failed: ' + err.message); }
    finally { setSaving(false); }
  }

  async function handleBoostUpdate() {
    if (!selected) return;
    setBoostSaving(true); setBoostMsg('');
    try {
      const boostBps = Math.round(editBoost * 100);
      const token = localStorage.getItem('admin_token');
      await fetch(`${API}/admin/users/${selected.id}/boost`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ boostBps })
      });
      setBoostMsg(`Boost updated to ${editBoost.toFixed(0)}%`);
      loadUser(selected.id); loadUsers();
    } catch (err) { setBoostMsg('Failed: ' + err.message); }
    finally { setBoostSaving(false); }
  }

  async function handleResetBoost() {
    if (!selected) return;
    setBoostSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API}/admin/users/${selected.id}/boost`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ boostBps: -1 })
      });
      setBoostMsg('Reset to platform boost');
      loadUser(selected.id); loadUsers();
    } catch (err) { setBoostMsg('Failed: ' + err.message); }
    finally { setBoostSaving(false); }
  }

  async function handleDelete() {
    if (!selected) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/admin/users/${selected.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setConfirmDelete(false);
      setSelected(null);
      await loadUsers();
    } catch (err) {
      setConfirmDelete(false);
      setMsg('Failed to delete: ' + err.message);
    }
  }

  async function handleSuspend(suspend) {
    if (!selected) return;
    try {
      await suspendUser(selected.id, suspend);
      setMsg(suspend ? 'User suspended' : 'User reactivated');
      setSelected(null);
      await loadUsers();
      const refreshed = await getUser(selected.id);
      setSelected(refreshed);
      setEditFee(parseFloat(refreshed.feePct !== null && refreshed.feePct !== undefined ? refreshed.feePct : 0).toString());
      setEditBoost((refreshed.effectiveBoostBps !== undefined ? refreshed.effectiveBoostBps : platformFeeBps) / 100);
    } catch (err) { setMsg('Failed: ' + err.message); }
  }

  async function handleClearStrikes() {
    if (!selected) return;
    setClearingStrikes(true);
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API}/admin/users/${selected.id}/clear-strikes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setMsg('Strikes cleared');
      loadUser(selected.id);
      loadUsers();
    } catch (err) { setMsg('Failed: ' + err.message); }
    finally { setClearingStrikes(false); }
  }

  function formatDate(ts) {
    return new Date(ts * 1000).toLocaleDateString('en-US', {
      month:'short', day:'numeric', year:'numeric'
    });
  }

  function formatDateTime(ts) {
    return new Date(ts * 1000).toLocaleDateString('en-US', {
      month:'short', day:'numeric', hour:'numeric', minute:'2-digit'
    });
  }

  const txColors = {
    deposit:'#00C48C', receive:'#00C48C', payment_received:'#00C48C',
    send:'#FF3B30', cashout_spend:'#FF9500', cashout_save:'#FF9500',
    payment:'#FF3B30', spend_to_earn:'#007AFF', save_to_spend:'#007AFF'
  };

  return (
    <div style={s.page}>
      <div style={s.left}>
        <h1 style={s.title}>Users</h1>
        <div style={s.searchWrap}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#AEAEB2" strokeWidth="2"
            style={{position:'absolute', left:12, top:'50%', transform:'translateY(-50%)'}}>
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input style={s.search} type="text" placeholder="Search users..."
            value={search} onChange={e => setSearch(e.target.value)}/>
        </div>

        <div style={s.listWrap}>
          {loading ? <div style={s.loading}>Loading...</div> : users.map(u => (
            <div key={u.id}
              style={{...s.userRow, ...(selected?.id===u.id ? s.userRowActive : {})}}
              onClick={() => loadUser(u.id)}>
              <div style={s.avatar}>
                {u.name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2)}
              </div>
              <div style={s.userInfo}>
                <div style={s.userName}>{u.name}</div>
                <div style={s.userSub}>{u.email||u.phone}</div>
              </div>
              <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4}}>
                <div style={{...s.userFee, color: u.isCustomFee ? '#FF9500' : '#007AFF'}}>
                  {parseFloat(u.feePct||0.50).toFixed(2)}%
                </div>
                {u.isCustomFee && <div style={s.customTag}>custom</div>}
                <div style={{display:'flex', gap:3}}>
                  {[0,1,2].map(i => (
                    <svg key={i} width="10" height="10" viewBox="0 0 24 24" fill="none"
                      stroke={(u.strikeCount||0) > i ? '#FF3B30' : '#E0E0E0'}
                      strokeWidth="3">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill={(u.strikeCount||0) > i ? '#FF3B30' : '#E0E0E0'} stroke="none"/>
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Waitlist */}
        <div style={{marginTop:16}}>
          <div style={{fontSize:11, fontWeight:600, color:'#AEAEB2', textTransform:'uppercase', letterSpacing:0.8, padding:'0 4px', marginBottom:8}}>
            Waiting ({waitlist.length})
          </div>
          {waitlist.map(w => (
            <div key={w.id}
              style={{...s.userRow, opacity:0.85, cursor:'pointer', ...(selectedWaitlist?.id===w.id ? {background:'#FFF5E6'} : {})}}
              onClick={() => { setSelectedWaitlist(w); setSelected(null); }}>
              <div style={{...s.avatar, background:'#FFF5E6', color:'#FF9500'}}>
                {w.name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2)}
              </div>
              <div style={s.userInfo}>
                <div style={s.userName}>{w.name}</div>
                <div style={s.userSub}>{w.email}</div>
              </div>
              <div style={{fontSize:10, fontWeight:600, color:'#FF9500', background:'#FFF5E6', padding:'3px 7px', borderRadius:6, flexShrink:0}}>
                WAITING
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={s.right}>
        {selectedWaitlist && !selected ? (
          <div>
            <div style={s.userHeader}>
              <div style={{...s.avatarLg, background:'#FFF5E6', color:'#FF9500'}}>
                {selectedWaitlist.name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2)}
              </div>
              <div style={{flex:1}}>
                <div style={s.userNameLg}>{selectedWaitlist.name}</div>
                <div style={s.userSubLg}>{selectedWaitlist.email}</div>
                <div style={s.userMeta}>
                  Joined {new Date(selectedWaitlist.created_at*1000).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                  <span style={{background:'#FFF5E6', color:'#FF9500', fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:6}}>Waiting</span>
                </div>
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>Details</div>
              <div style={s.cardRow}>
                <span style={s.cardLabel}>Name</span>
                <span style={s.cardValue}>{selectedWaitlist.name}</span>
              </div>
              <div style={s.cardRow}>
                <span style={s.cardLabel}>Email</span>
                <span style={s.cardValue}>{selectedWaitlist.email || '—'}</span>
              </div>
              <div style={s.cardRow}>
                <span style={s.cardLabel}>Business</span>
                <span style={s.cardValue}>{selectedWaitlist.business || '—'}</span>
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardTitle}>Actions</div>
              <button style={{...s.actionBtn, background:'#E6FAF5', color:'#00C48C', marginBottom:8, opacity:0.5, cursor:'not-allowed'}}
                disabled>
                ✉ Invite to platform — coming soon
              </button>
              <div style={{height:1, background:'#F5F5F7', margin:'8px 0'}}/>
              <button style={{...s.actionBtn, background:'#FFF0EF', color:'#FF3B30'}}
                onClick={async () => {
                  const token = localStorage.getItem('admin_token');
                  await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/admin/waitlist/${selectedWaitlist.id}`, {
                    method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
                  });
                  setSelectedWaitlist(null);
                  const r = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/admin/waitlist`, { headers: { Authorization: `Bearer ${token}` } });
                  setWaitlist(await r.json());
                }}>
                Remove from waitlist
              </button>
            </div>
          </div>
        ) : !selected ? (
          <div style={s.empty}>
            <div style={s.emptyIcon}>👥</div>
            <div style={s.emptyText}>Select a user to view details</div>
          </div>
        ) : (
          <>
            <div style={s.userHeader}>
              <div style={s.avatarLg}>
                {selected.name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2)}
              </div>
              <div style={{flex:1}}>
                <div style={s.userNameLg}>{selected.name}</div>
                <div style={s.userSubLg}>{selected.email||selected.phone}</div>
                <div style={s.userMeta}>
                  Joined {formatDate(selected.createdAt)} · {selected.country}
                  {selected.kycVerified==-1 && <span style={s.suspendedBadge}>Suspended</span>}
                </div>
              </div>
            </div>

            {msg && <div style={s.msg}>{msg}</div>}

            {/* Fee card */}
            <div style={s.card}>
  <div style={s.cardTitle}>Fee Setting</div>
  <div style={s.feeDisplay}>
    <div>
      <div style={s.feeLabel}>Fee rate</div>
      {selected.isCustomFee ? (
        <div style={{fontSize:11, color:'#FF9500', fontWeight:500, marginTop:2}}>
          Custom � platform default is {(parseFloat(selected.platformFeeBps||50)/100).toFixed(2)}%
        </div>
      ) : (
        <div style={{fontSize:11, color:'#AEAEB2', marginTop:2}}>Platform rate</div>
      )}
    </div>
    <div style={{...s.feeBig, color: selected.isCustomFee ? '#FF9500' : '#007AFF'}}>
      {parseFloat(editFee||0.50).toFixed(2)}%
    </div>
  </div>
  <input type="range" min="0" max="2" step="0.05"
    value={parseFloat(editFee||0.50)}
    style={s.slider}
    onChange={e => setEditFee(e.target.value)}
  />
  <div style={s.sliderTicks}>
    <span>0%</span><span>0.5%</span><span>1%</span><span>1.5%</span><span>2%</span>
  </div>
  <div style={s.presets}>
    {[0, 0.25, 0.50, 0.75, 1.00].map(pct => (
      <button key={pct}
        style={{...s.presetBtn, ...(Math.abs(parseFloat(editFee||0.50) - pct) < 0.01 ? s.presetBtnActive : {})}}
        onClick={() => setEditFee(pct.toString())}>
        {pct === 0 ? 'Free' : `${pct.toFixed(2)}%`}
      </button>
    ))}
  </div>
  <div style={s.feeActions}>
    <button style={s.updateBtn} onClick={handleFeeUpdate} disabled={saving}>
      {saving ? 'Saving...' : `Set to ${parseFloat(editFee||0.50).toFixed(2)}%`}
    </button>
    {selected.isCustomFee && (
      <button style={s.resetBtn} onClick={handleResetFee} disabled={saving}>
        Reset to platform
      </button>
    )}
  </div>
</div>


            {/* Boost card */}
            <div style={s.card}>
              <div style={s.cardTitle}>Boost Setting</div>
              <div style={s.feeDisplay}>
                <div>
                  <div style={s.feeLabel}>Boost rate</div>
                  <div style={{fontSize:11, color:'#AEAEB2', marginTop:2}}>
                    % of fees returned as BTC monthly
                  </div>
                </div>
                <div style={{...s.feeBig, color:'#FF9500'}}>
                  {(isNaN(editBoost) ? 20 : editBoost).toFixed(0)}%
                </div>
              </div>
              <input type="range" min="0" max="100" step="1"
                value={isNaN(editBoost) ? 20 : editBoost}
                style={{...s.slider, accentColor:'#FF9500'}}
                onChange={e => setEditBoost(parseFloat(e.target.value))}
              />
              <div style={s.sliderTicks}>
                <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
              </div>
              <div style={s.presets}>
                {[0, 10, 20, 30, 50].map(pct => (
                  <button key={pct}
                    style={{...s.presetBtn,
                      ...(Math.abs(editBoost - pct) < 0.5
                        ? {...s.presetBtnActive, background:'#FFF5E6', borderColor:'#FF9500', color:'#FF9500'}
                        : {})}}
                    onClick={() => setEditBoost(pct)}>
                    {pct === 0 ? 'Off' : `${pct}%`}
                  </button>
                ))}
              </div>
              {boostMsg && <div style={{fontSize:12, color:'#FF9500', marginBottom:8}}>{boostMsg}</div>}
              <div style={s.feeActions}>
                <button style={{...s.updateBtn, background:'#FF9500'}} onClick={handleBoostUpdate} disabled={boostSaving}>
                  {boostSaving ? 'Saving...' : `Set ${editBoost.toFixed(0)}% boost`}
                </button>
                <button style={s.resetBtn} onClick={handleResetBoost} disabled={boostSaving}>
                  Reset
                </button>
              </div>
            </div>

            {/* Balances */}
            <div style={s.card}>
              <div style={s.cardTitle}>Balances</div>
              <div style={{display:'flex', gap:12, marginBottom:4}}>
                <div style={{flex:1, background:'#F5F5F7', borderRadius:12, padding:16, textAlign:'center'}}>
                  <div style={{fontSize:11, color:'#6E6E73', fontWeight:500, marginBottom:6, textTransform:'uppercase', letterSpacing:0.5}}>Spend</div>
                  <div style={{fontSize:24, fontWeight:700, color:'#00C48C'}}>${selected.balance?.spendUsd||'0.00'}</div>
                  <div style={{fontSize:11, color:'#AEAEB2', marginTop:4}}>ledger</div>
                </div>
                <div style={{flex:1, background:'#F5F5F7', borderRadius:12, padding:16, textAlign:'center'}}>
                  <div style={{fontSize:11, color:'#6E6E73', fontWeight:500, marginBottom:6, textTransform:'uppercase', letterSpacing:0.5}}>On-Chain</div>
                  <div style={{fontSize:24, fontWeight:700, color:'#007AFF'}}>
                    {selected.balance?.liveUsdcBalance !== null && selected.balance?.liveUsdcBalance !== undefined
                      ? '$' + selected.balance.liveUsdcBalance
                      : '—'}
                  </div>
                  <div style={{fontSize:11, color:'#AEAEB2', marginTop:4}}>USDC live</div>
                </div>
                <div style={{flex:1, background:'#F5F5F7', borderRadius:12, padding:16, textAlign:'center'}}>
                  <div style={{fontSize:11, color:'#6E6E73', fontWeight:500, marginBottom:6, textTransform:'uppercase', letterSpacing:0.5}}>Stack</div>
                  <div style={{fontSize:24, fontWeight:700, color:'#FF9500'}}>${selected.balance?.stackUsd||'0.00'}</div>
                  <div style={{fontSize:11, color:'#AEAEB2', marginTop:4}}>{(selected.balance?.satoshis||0).toLocaleString()} sats</div>
                </div>
              </div>
              {selected.balance?.btcPrice && (
                <div style={{fontSize:11, color:'#AEAEB2', textAlign:'center', marginTop:8}}>
                  BTC @ ${parseFloat(selected.balance.btcPrice).toLocaleString()}
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={s.card}>
              <div style={s.cardTitle}>Account Actions</div>
              <button
                style={{...s.actionBtn, ...(selected.kycVerified==-1 ? s.actionBtnGreen : s.actionBtnRed)}}
                onClick={() => handleSuspend(selected.kycVerified != -1)}>
                {selected.kycVerified==-1 ? '✅ Reactivate account' : '🚫 Suspend account'}
              </button>
              <div style={{height:1, background:'#F5F5F7', margin:'16px 0'}}/>
              {!confirmDelete ? (
                <>
                  <div style={{fontSize:11, color:'#AEAEB2', marginBottom:8, textAlign:'center'}}>
                    Danger zone — this cannot be undone
                  </div>
                  <button
                    style={{...s.actionBtn, ...s.actionBtnDelete}}
                    onClick={() => setConfirmDelete(true)}>
                    🗑 Delete user permanently
                  </button>
                </>
              ) : (
                <div style={s.deleteConfirmBox}>
                  <div style={s.deleteConfirmIcon}>⚠️</div>
                  <div style={s.deleteConfirmTitle}>Are you absolutely sure?</div>
                  <div style={s.deleteConfirmText}>
                    This will permanently delete <strong>{selected.name}</strong> and all their data including transactions, bank accounts, BTC lots, and payment history. <strong>This cannot be recovered.</strong>
                  </div>
                  <button style={s.deleteConfirmBtn} onClick={handleDelete}>
                    Yes, permanently delete this user
                  </button>
                  <button style={s.deleteCancelBtn} onClick={() => setConfirmDelete(false)}>
                    Cancel — keep this user
                  </button>
                </div>
              )}
            </div>

            {/* Spam Reports */}
            <div style={s.card}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14}}>
                <div style={s.cardTitle}>Spam Reports</div>
                {selected.strikeCount > 0 && (
                  <button style={s.clearStrikesBtn} onClick={handleClearStrikes} disabled={clearingStrikes}>
                    {clearingStrikes ? 'Clearing...' : 'Clear strikes'}
                  </button>
                )}
              </div>

              <div style={{display:'flex', gap:10, marginBottom:16}}>
                <div style={{flex:1, background: selected.strikeCount >= 3 ? '#FFF0EF' : '#F5F5F7', borderRadius:12, padding:'14px 16px', textAlign:'center'}}>
                  <div style={{fontSize:11, color:'#AEAEB2', fontWeight:500, marginBottom:4, textTransform:'uppercase', letterSpacing:0.5}}>Strikes</div>
                  <div style={{fontSize:28, fontWeight:700, color: selected.strikeCount >= 3 ? '#FF3B30' : selected.strikeCount >= 2 ? '#FF9500' : '#1D1D1F'}}>
                    {selected.strikeCount || 0}
                  </div>
                  <div style={{fontSize:11, color:'#AEAEB2', marginTop:2}}>of 3</div>
                </div>
                <div style={{flex:1, background:'#F5F5F7', borderRadius:12, padding:'14px 16px', textAlign:'center'}}>
                  <div style={{fontSize:11, color:'#AEAEB2', fontWeight:500, marginBottom:4, textTransform:'uppercase', letterSpacing:0.5}}>Reports</div>
                  <div style={{fontSize:28, fontWeight:700, color:'#1D1D1F'}}>{spamReports.length}</div>
                  <div style={{fontSize:11, color:'#AEAEB2', marginTop:2}}>total</div>
                </div>
                <div style={{flex:1, background: selected.suspended ? '#FFF0EF' : '#E6FAF5', borderRadius:12, padding:'14px 16px', textAlign:'center'}}>
                  <div style={{fontSize:11, color:'#AEAEB2', fontWeight:500, marginBottom:4, textTransform:'uppercase', letterSpacing:0.5}}>Status</div>
                  <div style={{fontSize:13, fontWeight:700, color: selected.suspended ? '#FF3B30' : '#00C48C', marginTop:6}}>
                    {selected.suspended ? 'Suspended' : 'Active'}
                  </div>
                </div>
              </div>

              {spamReports.length === 0 ? (
                <div style={{fontSize:13, color:'#AEAEB2', textAlign:'center', padding:'12px 0'}}>No reports on this account</div>
              ) : (
                <div>
                  <div style={{fontSize:11, fontWeight:600, color:'#AEAEB2', textTransform:'uppercase', letterSpacing:0.8, marginBottom:8}}>Report History</div>
                  {spamReports.slice(0,10).map((r, i) => (
                    <div key={r.id || i} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #F5F5F7', fontSize:13}}>
                      <div>
                        <div style={{fontWeight:500, color:'#1D1D1F'}}>Request reported</div>
                        <div style={{fontSize:11, color:'#AEAEB2', marginTop:2, fontFamily:'monospace'}}>{r.request_id?.slice(0,16)}...</div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize:11, color:'#AEAEB2'}}>{r.reporter_ip}</div>
                        <div style={{fontSize:11, color:'#AEAEB2', marginTop:2}}>{formatDateTime(r.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Transactions */}
            <div style={s.card}>
              <div style={s.cardTitle}>Recent Transactions</div>
              {selected.transactions?.length === 0 ? (
                <div style={s.noTx}>No transactions yet</div>
              ) : selected.transactions?.slice(0,15).map(tx => (
                <div key={tx.id} style={s.txRow}>
                  <div style={{...s.txDot,
                    background:(txColors[tx.type]||'#AEAEB2')+'20',
                    color:txColors[tx.type]||'#AEAEB2'}}>
                    {['deposit','receive','payment_received'].includes(tx.type) ? '+' : '-'}
                  </div>
                  <div style={s.txInfo}>
                    <div style={s.txType}>{({
                      spend_to_save: 'spend to earn',
                      spend_to_stack: 'spend to earn',
                      save_to_spend: 'earn to spend',
                      stack_to_spend: 'earn to spend',
                    })[tx.type] || tx.type.replace(/_/g,' ')}</div>
                    <div style={s.txDate}>{formatDateTime(tx.createdAt)}</div>
                  </div>
                  <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end'}}>
                    <div style={{...s.txAmount, color:txColors[tx.type]||'#AEAEB2'}}>
                      ${tx.amountUsd}
                    </div>
                    <div style={{fontSize:11, color:'#AEAEB2'}}>fee ${tx.feeUsd}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page:          {display:'flex', gap:20, height:'calc(100vh - 64px)'},
  left:          {width:300, flexShrink:0, display:'flex', flexDirection:'column'},
  right:         {flex:1, overflowY:'auto', paddingBottom:40},
  title:         {fontSize:24, fontWeight:700, marginBottom:16},
  searchWrap:    {position:'relative', marginBottom:12},
  search:        {width:'100%', padding:'10px 14px 10px 36px', background:'#fff', border:'1.5px solid #F0F0F0', borderRadius:12, fontSize:14, outline:'none'},
  listWrap:      {flex:1, overflowY:'auto'},
  loading:       {color:'#6E6E73', padding:20, fontSize:14},
  userRow:       {display:'flex', alignItems:'center', gap:10, padding:'12px', borderRadius:12, cursor:'pointer', marginBottom:2},
  userRowActive: {background:'#EBF4FF'},
  avatar:        {width:36, height:36, borderRadius:'50%', background:'#007AFF', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:600, flexShrink:0},
  userInfo:      {flex:1, minWidth:0},
  userName:      {fontSize:14, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'},
  userSub:       {fontSize:12, color:'#6E6E73', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'},
  userFee:       {fontSize:12, fontWeight:600},
  customTag:     {fontSize:10, color:'#FF9500', fontWeight:500},
  empty:         {display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'60%', gap:12},
  emptyIcon:     {fontSize:48},
  emptyText:     {fontSize:15, color:'#6E6E73'},
  userHeader:    {display:'flex', alignItems:'center', gap:16, marginBottom:20},
  avatarLg:      {width:56, height:56, borderRadius:'50%', background:'#007AFF', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:600, flexShrink:0},
  userNameLg:    {fontSize:20, fontWeight:600},
  userSubLg:     {fontSize:14, color:'#6E6E73'},
  userMeta:      {fontSize:12, color:'#AEAEB2', marginTop:4, display:'flex', alignItems:'center', gap:8},
  suspendedBadge:{background:'#FFF0EF', color:'#FF3B30', fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:6},
  msg:           {background:'#EBF4FF', border:'1px solid #C5DEFF', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#007AFF', marginBottom:16},
  card:          {background:'#fff', borderRadius:16, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  cardTitle:     {fontSize:12, fontWeight:600, color:'#AEAEB2', marginBottom:14, textTransform:'uppercase', letterSpacing:0.8},
  feeRow:        {display:'flex', justifyContent:'space-between', alignItems:'flex-start'},
  feeLabel:      {fontSize:12, color:'#6E6E73', marginBottom:4},
  feeBig:        {fontSize:28, fontWeight:700},
  feeEdit:       {display:'flex', alignItems:'center', gap:6},
  feeInput:      {width:72, padding:'10px', border:'1.5px solid #F0F0F0', borderRadius:10, fontSize:16, fontWeight:600, outline:'none', textAlign:'center'},
  feePct:        {fontSize:16, fontWeight:600},
  saveBtn:       {padding:'10px 16px', background:'#007AFF', color:'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer'},
  resetFeeBtn:   {marginTop:12, padding:'8px 14px', background:'#FFF9EC', border:'1px solid #FFE8A3', borderRadius:8, fontSize:12, fontWeight:500, color:'#B87A00', cursor:'pointer', width:'100%'},
  walletAddr:    {fontSize:12, fontFamily:'monospace', color:'#1D1D1F', wordBreak:'break-all', marginBottom:8, background:'#F5F5F7', padding:'10px', borderRadius:8},
  link:          {fontSize:13, color:'#007AFF', textDecoration:'none'},
  cardRow:       {display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #F5F5F7'},
  cardLabel:     {fontSize:13, color:'#6E6E73'},
  cardValue:     {fontSize:13, fontWeight:500, color:'#1D1D1F'},
  actionBtn:     {padding:'12px 20px', border:'none', borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', width:'100%'},
  actionBtnRed:  {background:'#FFF0EF', color:'#FF3B30'},
  actionBtnGreen:{background:'#E6FAF5', color:'#00C48C'},
  actionBtnDelete:{background:'#fff', color:'#FF3B30', border:'1.5px solid #FF3B30', marginTop:4, fontSize:13, fontWeight:500, opacity:0.8},
  deleteConfirmBox:  {background:'#FFF0EF', border:'1.5px solid #FFD0CE', borderRadius:12, padding:20, textAlign:'center'},
  deleteConfirmIcon: {fontSize:32, marginBottom:8},
  deleteConfirmTitle:{fontSize:15, fontWeight:700, color:'#FF3B30', marginBottom:8},
  deleteConfirmText: {fontSize:13, color:'#1D1D1F', lineHeight:1.6, marginBottom:16},
  deleteConfirmBtn:  {width:'100%', padding:13, background:'#FF3B30', color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer', marginBottom:8},
  deleteCancelBtn:   {width:'100%', padding:11, background:'#fff', color:'#6E6E73', border:'1.5px solid #E5E5EA', borderRadius:10, fontSize:13, fontWeight:500, cursor:'pointer'},
  noTx:          {fontSize:14, color:'#AEAEB2', textAlign:'center', padding:'20px 0'},
  txRow:         {display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid #F5F5F7'},
  txDot:         {width:32, height:32, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:600, flexShrink:0},
  txInfo:        {flex:1},
  txType:        {fontSize:13, fontWeight:500, textTransform:'capitalize'},
  txDate:        {fontSize:11, color:'#AEAEB2', marginTop:2},
  txAmount:      {fontSize:14, fontWeight:600},
  clearStrikesBtn: {padding:'6px 12px', background:'#FFF9EC', border:'1px solid #FFE8A3', borderRadius:8, fontSize:12, fontWeight:500, color:'#B87A00', cursor:'pointer'},	feeDisplay:     {display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16},
feeLabel:       {fontSize:13, fontWeight:600, color:'#1D1D1F'},
feeBig:         {fontSize:32, fontWeight:700},
slider:         {width:'100%', accentColor:'#007AFF', height:4, marginBottom:6},
sliderTicks:    {display:'flex', justifyContent:'space-between', fontSize:10, color:'#AEAEB2', marginBottom:12},
presets:        {display:'flex', gap:6, marginBottom:16, flexWrap:'wrap'},
presetBtn:      {padding:'6px 12px', background:'#F5F5F7', border:'1.5px solid transparent', borderRadius:8, fontSize:12, fontWeight:500, color:'#6E6E73', cursor:'pointer'},
presetBtnActive:{background:'#EBF4FF', borderColor:'#007AFF', color:'#007AFF'},
feeActions:     {display:'flex', gap:8},
updateBtn:      {flex:2, padding:'10px 16px', background:'#007AFF', color:'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer'},
resetBtn:       {flex:1, padding:'10px 12px', background:'#FFF9EC', border:'1px solid #FFE8A3', borderRadius:10, fontSize:12, fontWeight:500, color:'#B87A00', cursor:'pointer'},
};
